import { parameterUtils, type ModelData, type ModelParameter } from '@/shared/stores/model-utils'
import type { DimensionHandler } from '@/shared/stores/create-model-store'

function getParam(params: ModelParameter[], id: string) {
  return parameterUtils.findParameter(params, id)
}

function updateParam(model: ModelData, id: string, updates: Partial<ModelParameter>) {
  model.parameters = parameterUtils.updateParameterInArray(model.parameters!, id, updates)
}

function validateDimensions(width: number, height: number) {
  return {
    width: Math.max(2000, Math.min(4000, width)),
    height: Math.max(1800, Math.min(4500, height)),
  }
}

function shiftComponentsRight(model: ModelData, componentIds: string[], widthDifference: number) {
  componentIds.forEach((componentId) => {
    const component = getParam(model.parameters!, componentId)
    if (component?.position) {
      updateParam(model, componentId, {
        position: { ...component.position, x: (component.position.x || 0) + widthDifference } as ModelParameter['position'],
      })
    }
  })
}

function adjustHinges(model: ModelData, positionId: string, newFrameHeight: number) {
  const frame = getParam(model.parameters!, positionId)
  if (!frame?.hardware?.hinges) return
  const oldFrameHeight = frame.height || newFrameHeight
  const updatedHinges = frame.hardware.hinges.map((hinge) => {
    const isBottomHinge1 = hinge.positionId?.includes('bottom_hinge_1')
    const isBottomHinge2 = hinge.positionId?.includes('bottom_hinge_2')
    const relativePosition = oldFrameHeight > 0 ? hinge.position.y / oldFrameHeight : 0
    let newY: number
    if (isBottomHinge1) newY = Math.round(newFrameHeight * 0.75)
    else if (isBottomHinge2) newY = Math.round(newFrameHeight * 0.85)
    else newY = Math.max(60, Math.min(newFrameHeight - (hinge.height || 100) - 60, relativePosition * newFrameHeight))
    return { ...hinge, position: { ...hinge.position, y: newY } }
  })
  updateParam(model, positionId, { hardware: { ...frame.hardware, hinges: updatedHinges } })
}

function recalculatePositions(model: ModelData, recalculateHeights = false) {
  const frameThickness = 60
  const totalHeight = model.height as number

  const sections = {
    leftFixed: getParam(model.parameters!, 'bottom_left_frame_profile')?.width || 440,
    door1: getParam(model.parameters!, 'door_1_bottom_frame_profile')?.width || 810,
    door2: getParam(model.parameters!, 'door_2_bottom_frame_profile')?.width || 810,
    rightFixed: getParam(model.parameters!, 'bottom_right_frame_profile')?.width || 440,
  }

  const positions = {
    leftFixed: 0,
    door1: sections.leftFixed + frameThickness,
    door2: sections.leftFixed + frameThickness + sections.door1 + frameThickness,
    rightFixed: sections.leftFixed + frameThickness + sections.door1 + frameThickness + sections.door2 + frameThickness,
  }

  const topFrame = getParam(model.parameters!, 'top_frame_profile')
  const doorTopGlassPanel = getParam(model.parameters!, 'door_top_glass_panel')
  const middleHorizontalFrame = getParam(model.parameters!, 'middle_horizontal_frame_profile')

  const transomH = (topFrame?.height || 60) + (doorTopGlassPanel?.height || 200) + (middleHorizontalFrame?.height || 60)
  const doorYStart = transomH
  const bottomY = totalHeight - (topFrame?.height || 60)

  const currentVerticalFrameHeight = recalculateHeights
    ? totalHeight - (topFrame?.height || 60)
    : getParam(model.parameters!, 'left_vertical_frame_profile')?.height || totalHeight - (topFrame?.height || 60)

  const door1LeftFrame = getParam(model.parameters!, 'door_1_left_frame_profile')
  const doorMainPanelHeight = bottomY - doorYStart + (getParam(model.parameters!, 'door_1_top_frame_profile')?.height || 60)
  const currentDoorFrameHeight = recalculateHeights
    ? door1LeftFrame?.height !== undefined ? door1LeftFrame.height : doorMainPanelHeight
    : door1LeftFrame?.height || doorMainPanelHeight

  const doorGlassPanel1 = getParam(model.parameters!, 'door_glass_panel_1')
  const doorBottomFrame = getParam(model.parameters!, 'door_1_bottom_frame_profile')
  const doorTopFrame = getParam(model.parameters!, 'door_1_top_frame_profile')
  const currentDoorGlassHeight = recalculateHeights
    ? doorGlassPanel1?.height !== undefined ? doorGlassPanel1.height
      : currentDoorFrameHeight - (doorTopFrame?.height || 60) - (doorBottomFrame?.height || 60)
    : doorGlassPanel1?.height || 1765

  const currentFixedPanelHeight = recalculateHeights
    ? totalHeight - frameThickness * 2
    : getParam(model.parameters!, 'left_glass_panel')?.height || totalHeight - frameThickness * 2

  const calculatedTotalWidth = sections.leftFixed + sections.door1 + sections.door2 + sections.rightFixed + frameThickness * 3
  const finalTotalWidth = (model.width as number) || calculatedTotalWidth
  if (!model.width) model.width = finalTotalWidth

  updateParam(model, 'top_frame_profile', { width: finalTotalWidth })
  updateParam(model, 'right_frame_profile', { position: { x: finalTotalWidth - frameThickness, y: 0, z: 0 } })

  const bottomFrameY = recalculateHeights ? bottomY : getParam(model.parameters!, 'bottom_left_frame_profile')?.position?.y || bottomY

  updateParam(model, 'bottom_left_frame_profile', { position: { x: positions.leftFixed, y: bottomFrameY, z: 0 } })
  updateParam(model, 'door_1_bottom_frame_profile', { position: { x: positions.door1, y: bottomFrameY, z: 0 } })
  updateParam(model, 'door_2_bottom_frame_profile', { position: { x: positions.door2, y: bottomFrameY, z: 0 } })
  updateParam(model, 'bottom_right_frame_profile', { position: { x: positions.rightFixed, y: bottomFrameY, z: 0 } })

  updateParam(model, 'left_vertical_frame_profile', { position: { x: positions.door1 - frameThickness, y: frameThickness, z: 0 }, height: currentVerticalFrameHeight })
  updateParam(model, 'right_vertical_frame_profile', { position: { x: positions.rightFixed, y: frameThickness, z: 0 }, height: currentVerticalFrameHeight })

  const door1RightFrame = getParam(model.parameters!, 'door_1_right_frame_profile')
  const door2LeftFrame = getParam(model.parameters!, 'door_2_left_frame_profile')
  const door2RightFrame = getParam(model.parameters!, 'door_2_right_frame_profile')
  const middleVerticalFrameCenter = getParam(model.parameters!, 'middle_vertical_frame_profile_center')

  const pY = (frame: ModelParameter | undefined) => recalculateHeights ? frame?.position?.y : doorYStart

  updateParam(model, 'door_1_left_frame_profile', { position: { x: positions.door1, y: pY(door1LeftFrame) ?? doorYStart, z: 0 }, height: currentDoorFrameHeight })
  updateParam(model, 'door_1_right_frame_profile', { position: { x: positions.door1 + sections.door1 - frameThickness - frameThickness / 2, y: pY(door1RightFrame) ?? doorYStart, z: 0 }, height: currentDoorFrameHeight })
  updateParam(model, 'middle_vertical_frame_profile_center', { position: { x: positions.door1 + sections.door1, y: pY(middleVerticalFrameCenter) ?? doorYStart, z: 0 }, height: currentDoorFrameHeight })
  updateParam(model, 'door_2_left_frame_profile', { position: { x: positions.door2, y: pY(door2LeftFrame) ?? doorYStart, z: 0 }, height: currentDoorFrameHeight })
  updateParam(model, 'door_2_right_frame_profile', { position: { x: positions.rightFixed - frameThickness - frameThickness / 2, y: pY(door2RightFrame) ?? doorYStart, z: 0 }, height: currentDoorFrameHeight })

  const door1TopF = getParam(model.parameters!, 'door_1_top_frame_profile')
  const door2TopF = getParam(model.parameters!, 'door_2_top_frame_profile')
  updateParam(model, 'door_1_top_frame_profile', { position: { x: positions.door1, y: (recalculateHeights ? door1TopF?.position?.y : doorYStart) ?? doorYStart, z: 0 }, width: sections.door1 })
  updateParam(model, 'door_2_top_frame_profile', { position: { x: positions.door2, y: (recalculateHeights ? door2TopF?.position?.y : doorYStart) ?? doorYStart, z: 0 }, width: sections.door2 })

  updateParam(model, 'middle_horizontal_frame_profile', { position: { x: positions.door1, y: (topFrame?.height || 60) + (doorTopGlassPanel?.height || 200), z: 0 }, width: sections.door1 + sections.door2 })

  updateParam(model, 'left_glass_panel', { position: { x: frameThickness, y: frameThickness, z: 0 }, width: sections.leftFixed - frameThickness * 2, height: currentFixedPanelHeight })
  updateParam(model, 'right_glass_panel', { position: { x: positions.rightFixed + frameThickness, y: frameThickness, z: 0 }, width: sections.rightFixed - frameThickness * 2, height: currentFixedPanelHeight })

  updateParam(model, 'door_top_glass_panel', { position: { x: positions.door1, y: frameThickness, z: 0 }, width: sections.door1 + sections.door2 })

  const doorGlassPanel2 = getParam(model.parameters!, 'door_glass_panel_2')
  const pGY1 = recalculateHeights ? doorGlassPanel1?.position?.y : doorGlassPanel1?.position?.y || doorYStart + frameThickness
  const pGY2 = recalculateHeights ? doorGlassPanel2?.position?.y : doorGlassPanel2?.position?.y || doorYStart + frameThickness

  updateParam(model, 'door_glass_panel_1', { position: { x: positions.door1 + frameThickness, y: pGY1 ?? doorYStart + frameThickness, z: 0 }, width: sections.door1 - frameThickness * 2 - frameThickness / 2, height: currentDoorGlassHeight })
  updateParam(model, 'door_glass_panel_2', { position: { x: positions.door2 + frameThickness, y: pGY2 ?? doorYStart + frameThickness, z: 0 }, width: sections.door2 - frameThickness * 2 - frameThickness / 2, height: currentDoorGlassHeight })
}

const totalWidth: DimensionHandler = (model, _initial, numValue, options) => {
  const { cascadeUpdates = true } = options
  const validated = validateDimensions(numValue, model.height as number)
  const finalWidth = validated.width
  const prevTotalWidth = (model.width as number) || 2500
  const widthDifference = finalWidth - prevTotalWidth
  const frameThickness = 60

  const leftBottomFrame = getParam(model.parameters!, 'bottom_left_frame_profile')
  const rightBottomFrame = getParam(model.parameters!, 'bottom_right_frame_profile')
  const currentLeftWidth = leftBottomFrame?.width || 440
  const currentRightWidth = rightBottomFrame?.width || 440
  const leftIncrease = Math.floor(widthDifference / 2)
  const rightIncrease = widthDifference - leftIncrease
  const newLeftWidth = currentLeftWidth + leftIncrease
  const newRightWidth = currentRightWidth + rightIncrease

  if (newLeftWidth < 200 || newRightWidth < 200) return model

  model.width = finalWidth
  updateParam(model, 'bottom_left_frame_profile', { width: newLeftWidth })
  updateParam(model, 'bottom_right_frame_profile', { width: newRightWidth })

  const leftFrame = getParam(model.parameters!, 'left_frame_profile')
  const leftVerticalFrame = getParam(model.parameters!, 'left_vertical_frame_profile')
  updateParam(model, 'left_glass_panel', { width: newLeftWidth - (leftFrame?.width || frameThickness) - (leftVerticalFrame?.width || frameThickness) })

  if (cascadeUpdates) {
    const componentsToShift = [
      'door_1_bottom_frame_profile', 'door_1_left_frame_profile', 'door_1_right_frame_profile',
      'door_1_top_frame_profile', 'door_glass_panel_1', 'middle_vertical_frame_profile_center',
      'door_2_bottom_frame_profile', 'door_2_left_frame_profile', 'door_2_right_frame_profile',
      'door_2_top_frame_profile', 'door_glass_panel_2', 'right_vertical_frame_profile',
      'right_glass_panel', 'bottom_right_frame_profile', 'door_top_glass_panel',
      'middle_horizontal_frame_profile', 'right_frame_profile',
    ]

    const lv = getParam(model.parameters!, 'left_vertical_frame_profile')
    if (lv) {
      updateParam(model, 'left_vertical_frame_profile', {
        position: { x: newLeftWidth - frameThickness, y: lv.position?.y || frameThickness, z: lv.position?.z || 0 },
      })
    }

    shiftComponentsRight(model, componentsToShift, leftIncrease)

    const rightFrame2 = getParam(model.parameters!, 'right_frame_profile')
    const rightVerticalFrame = getParam(model.parameters!, 'right_vertical_frame_profile')
    updateParam(model, 'right_glass_panel', { width: newRightWidth - (rightFrame2?.width || frameThickness) - (rightVerticalFrame?.width || frameThickness) })
    updateParam(model, 'top_frame_profile', { width: finalWidth })
    updateParam(model, 'right_frame_profile', { position: { x: finalWidth - frameThickness, y: 0, z: 0 } })

    const centerFrame1 = getParam(model.parameters!, 'door_1_bottom_frame_profile')
    const centerFrame2 = getParam(model.parameters!, 'door_2_bottom_frame_profile')
    model.doorConfiguration = {
      ...model.doorConfiguration!,
      totalDoorWidth: finalWidth,
      centerDoorWidth: (centerFrame1?.width || 810) + (centerFrame2?.width || 810),
      individualDoorWidth: [centerFrame1?.width || 810, centerFrame2?.width || 810],
      leftFixedWidth: newLeftWidth,
      rightFixedWidth: newRightWidth,
    }
  }

  return model
}

const totalHeight: DimensionHandler = (model, _initial, numValue, options) => {
  const { cascadeUpdates = true } = options
  const validated = validateDimensions(model.width as number, numValue)
  const finalHeight = validated.height
  model.height = finalHeight
  updateParam(model, 'left_frame_profile', { height: finalHeight })
  updateParam(model, 'right_frame_profile', { height: finalHeight })

  if (cascadeUpdates) {
    const preservedPositionsX: Record<string, number> = {}
    model.parameters!.forEach((param) => {
      if (param?.position?.x !== undefined) preservedPositionsX[param.positionId!] = param.position.x
    })

    const doorTopGlassPanel = getParam(model.parameters!, 'door_top_glass_panel')
    const middleHorizontalFrame = getParam(model.parameters!, 'middle_horizontal_frame_profile')
    const preservedTransomHeight = doorTopGlassPanel?.height
    const preservedTransomY = doorTopGlassPanel?.position?.y
    const preservedMiddleFrameHeight = middleHorizontalFrame?.height
    const preservedMiddleFrameY = middleHorizontalFrame?.position?.y

    recalculatePositions(model, true)

    Object.keys(preservedPositionsX).forEach((positionId) => {
      const component = getParam(model.parameters!, positionId)
      if (component?.position && preservedPositionsX[positionId] !== undefined) {
        updateParam(model, positionId, { position: { ...component.position, x: preservedPositionsX[positionId] } as ModelParameter['position'] })
      }
    })

    if (preservedTransomHeight !== undefined) {
      updateParam(model, 'door_top_glass_panel', { height: preservedTransomHeight, position: { ...getParam(model.parameters!, 'door_top_glass_panel')?.position, y: preservedTransomY } as ModelParameter['position'] })
    }
    if (preservedMiddleFrameHeight !== undefined) {
      updateParam(model, 'middle_horizontal_frame_profile', { height: preservedMiddleFrameHeight, position: { ...getParam(model.parameters!, 'middle_horizontal_frame_profile')?.position, y: preservedMiddleFrameY } as ModelParameter['position'] })
    }

    const topFrame = getParam(model.parameters!, 'top_frame_profile')
    const frameHeight = topFrame?.height || 60
    const calculatedTransomHeight = frameHeight + (preservedTransomHeight || 200) + (preservedMiddleFrameHeight || 60)
    const doorMainPanelHeight = finalHeight - calculatedTransomHeight

    updateParam(model, 'door_1_left_frame_profile', { height: doorMainPanelHeight })
    adjustHinges(model, 'door_1_left_frame_profile', doorMainPanelHeight)
    updateParam(model, 'door_1_right_frame_profile', { height: doorMainPanelHeight })
    updateParam(model, 'door_2_left_frame_profile', { height: doorMainPanelHeight })
    updateParam(model, 'door_2_right_frame_profile', { height: doorMainPanelHeight })
    adjustHinges(model, 'door_2_right_frame_profile', doorMainPanelHeight)
    updateParam(model, 'middle_vertical_frame_profile_center', { height: doorMainPanelHeight })

    const door1TopFrame = getParam(model.parameters!, 'door_1_top_frame_profile')
    const door1BottomFrame = getParam(model.parameters!, 'door_1_bottom_frame_profile')
    const doorGlassPanelHeight = doorMainPanelHeight - (door1TopFrame?.height || 60) - (door1BottomFrame?.height || 60)
    updateParam(model, 'door_glass_panel_1', { height: doorGlassPanelHeight })
    updateParam(model, 'door_glass_panel_2', { height: doorGlassPanelHeight })

    const panel = getParam(model.parameters!, 'door_glass_panel_2')
    if (panel?.hardware?.handles) {
      const newHandleY = Math.round(panel.height / 2)
      const updatedHandles = panel.hardware.handles.map((h) => ({ ...h, position: { ...h.position, y: newHandleY } }))
      updateParam(model, 'door_glass_panel_2', { hardware: { ...panel.hardware, handles: updatedHandles } })
    }
  }

  return model
}

const handleHeight: DimensionHandler = (model, _initial, numValue, options) => {
  const { positionId } = options as { positionId: string }
  const param = getParam(model.parameters!, positionId)
  if (!param?.hardware?.handles?.[0]) return model
  updateParam(model, positionId, {
    hardware: { ...param.hardware, handles: [{ ...param.hardware.handles[0], position: { ...param.hardware.handles[0].position, y: numValue } }] },
  })
  return model
}

const bottomFrame1Width: DimensionHandler = (model, _initial, numValue, options) => {
  const { cascadeUpdates = true } = options
  const frameThickness = 60
  const clampedWidth = Math.max(300, Math.min(1500, numValue))

  if (cascadeUpdates) {
    const centerFrame1 = getParam(model.parameters!, 'door_1_bottom_frame_profile')
    const oldWidth = centerFrame1?.width || 810
    const widthDifference = clampedWidth - oldWidth

    updateParam(model, 'door_1_bottom_frame_profile', { width: clampedWidth })
    updateParam(model, 'door_1_top_frame_profile', { width: clampedWidth })
    updateParam(model, 'door_glass_panel_1', { width: clampedWidth - frameThickness * 2 - frameThickness / 2 })

    const door1Left = getParam(model.parameters!, 'door_1_left_frame_profile')
    if (door1Left) {
      updateParam(model, 'door_1_right_frame_profile', {
        position: { x: (door1Left.position?.x || 0) + clampedWidth - frameThickness - frameThickness / 2, y: getParam(model.parameters!, 'door_1_right_frame_profile')?.position?.y || 0, z: 0 },
      })
    }

    const doorTopGlassPanel = getParam(model.parameters!, 'door_top_glass_panel')
    const middleHorizontalFrame = getParam(model.parameters!, 'middle_horizontal_frame_profile')
    const preservedTransomX = doorTopGlassPanel?.position?.x
    const preservedMiddleFrameX = middleHorizontalFrame?.position?.x

    const componentsToShift = ['middle_vertical_frame_profile_center', 'door_2_bottom_frame_profile', 'door_2_left_frame_profile', 'door_2_right_frame_profile', 'door_2_top_frame_profile', 'door_glass_panel_2', 'right_vertical_frame_profile', 'right_glass_panel', 'bottom_right_frame_profile', 'right_frame_profile']
    shiftComponentsRight(model, componentsToShift, widthDifference)

    if (preservedTransomX !== undefined) updateParam(model, 'door_top_glass_panel', { position: { ...getParam(model.parameters!, 'door_top_glass_panel')?.position, x: preservedTransomX } as ModelParameter['position'] })
    if (preservedMiddleFrameX !== undefined) updateParam(model, 'middle_horizontal_frame_profile', { position: { ...getParam(model.parameters!, 'middle_horizontal_frame_profile')?.position, x: preservedMiddleFrameX } as ModelParameter['position'] })

    const leftFixedWidth = getParam(model.parameters!, 'bottom_left_frame_profile')?.width || 440
    const rightFixedWidth = getParam(model.parameters!, 'bottom_right_frame_profile')?.width || 440
    const currentCenterWidth2 = getParam(model.parameters!, 'door_2_bottom_frame_profile')?.width || 810
    const newTotalWidth = leftFixedWidth + clampedWidth + currentCenterWidth2 + rightFixedWidth
    const validated = validateDimensions(newTotalWidth, model.height as number)
    model.width = validated.width
    updateParam(model, 'top_frame_profile', { width: validated.width })
    updateParam(model, 'right_frame_profile', { position: { x: validated.width - frameThickness, y: 0, z: 0 } })

    const totalDoorWidth = clampedWidth + currentCenterWidth2
    updateParam(model, 'door_top_glass_panel', { width: totalDoorWidth })
    updateParam(model, 'middle_horizontal_frame_profile', { width: totalDoorWidth })

    model.doorConfiguration = { ...model.doorConfiguration!, totalDoorWidth: validated.width, centerDoorWidth: totalDoorWidth, leftDoorWidth: clampedWidth, individualDoorWidth: [clampedWidth, currentCenterWidth2] }
  } else {
    updateParam(model, 'door_1_bottom_frame_profile', { width: clampedWidth })
  }

  return model
}

const bottomFrame2Width: DimensionHandler = (model, _initial, numValue, options) => {
  const { cascadeUpdates = true } = options
  const frameThickness = 60
  const clampedWidth = Math.max(300, Math.min(1500, numValue))

  if (cascadeUpdates) {
    const centerFrame2 = getParam(model.parameters!, 'door_2_bottom_frame_profile')
    const oldWidth = centerFrame2?.width || 810
    const widthDifference = clampedWidth - oldWidth

    updateParam(model, 'door_2_bottom_frame_profile', { width: clampedWidth })
    updateParam(model, 'door_2_top_frame_profile', { width: clampedWidth })
    updateParam(model, 'door_glass_panel_2', { width: clampedWidth - frameThickness * 2 - frameThickness / 2 })

    const door2Left = getParam(model.parameters!, 'door_2_left_frame_profile')
    if (door2Left) {
      updateParam(model, 'door_2_right_frame_profile', {
        position: { x: (door2Left.position?.x || 0) + clampedWidth - frameThickness - frameThickness / 2, y: getParam(model.parameters!, 'door_2_right_frame_profile')?.position?.y || 0, z: 0 },
      })
    }

    const doorTopGlassPanel = getParam(model.parameters!, 'door_top_glass_panel')
    const middleHorizontalFrame = getParam(model.parameters!, 'middle_horizontal_frame_profile')
    const preservedTransomX = doorTopGlassPanel?.position?.x
    const preservedMiddleFrameX = middleHorizontalFrame?.position?.x

    const componentsToShift = ['right_vertical_frame_profile', 'right_glass_panel', 'bottom_right_frame_profile', 'right_frame_profile']
    shiftComponentsRight(model, componentsToShift, widthDifference)

    if (preservedTransomX !== undefined) updateParam(model, 'door_top_glass_panel', { position: { ...getParam(model.parameters!, 'door_top_glass_panel')?.position, x: preservedTransomX } as ModelParameter['position'] })
    if (preservedMiddleFrameX !== undefined) updateParam(model, 'middle_horizontal_frame_profile', { position: { ...getParam(model.parameters!, 'middle_horizontal_frame_profile')?.position, x: preservedMiddleFrameX } as ModelParameter['position'] })

    const leftFixedWidth = getParam(model.parameters!, 'bottom_left_frame_profile')?.width || 440
    const rightFixedWidth = getParam(model.parameters!, 'bottom_right_frame_profile')?.width || 440
    const currentCenterWidth1 = getParam(model.parameters!, 'door_1_bottom_frame_profile')?.width || 810
    const newTotalWidth = leftFixedWidth + currentCenterWidth1 + clampedWidth + rightFixedWidth
    const validated = validateDimensions(newTotalWidth, model.height as number)
    model.width = validated.width
    updateParam(model, 'top_frame_profile', { width: validated.width })
    updateParam(model, 'right_frame_profile', { position: { x: validated.width - frameThickness, y: 0, z: 0 } })

    const totalDoorWidth = currentCenterWidth1 + clampedWidth
    updateParam(model, 'door_top_glass_panel', { width: totalDoorWidth })
    updateParam(model, 'middle_horizontal_frame_profile', { width: totalDoorWidth })

    model.doorConfiguration = { ...model.doorConfiguration!, totalDoorWidth: validated.width, centerDoorWidth: totalDoorWidth, rightDoorWidth: clampedWidth, individualDoorWidth: [currentCenterWidth1, clampedWidth] }
  } else {
    updateParam(model, 'door_2_bottom_frame_profile', { width: clampedWidth })
  }

  return model
}

const transomHeight: DimensionHandler = (model, _initial, numValue, options) => {
  const { cascadeUpdates = true } = options
  const clampedHeight = Math.max(150, Math.min(800, numValue))
  const topFrame = getParam(model.parameters!, 'top_frame_profile')
  const frameHeight = topFrame?.height || 60
  const transomeBottomFrame = getParam(model.parameters!, 'middle_horizontal_frame_profile')
  const doorTopGlassPanel = getParam(model.parameters!, 'door_top_glass_panel')

  const currentTotalTransomHeight = frameHeight + (doorTopGlassPanel?.height || 200) + (transomeBottomFrame?.height || 60)
  const heightDifference = clampedHeight - currentTotalTransomHeight
  const newTransomGlassHeight = clampedHeight - frameHeight - (transomeBottomFrame?.height || 60)
  updateParam(model, 'door_top_glass_panel', { height: newTransomGlassHeight })

  if (cascadeUpdates) {
    const preservedPositionsX: Record<string, number> = {}
    model.parameters!.forEach((param) => {
      if (param?.position?.x !== undefined) preservedPositionsX[param.positionId!] = param.position.x
    })

    const doorFrames = ['door_1_left_frame_profile', 'door_1_right_frame_profile', 'door_2_left_frame_profile', 'door_2_right_frame_profile']
    doorFrames.forEach((id) => {
      const f = getParam(model.parameters!, id)
      if (f?.position) {
        updateParam(model, id, { position: { ...f.position, y: (f.position.y || 0) + heightDifference } as ModelParameter['position'], height: (f.height || 0) - heightDifference })
      }
    })

    adjustHinges(model, 'door_1_left_frame_profile', getParam(model.parameters!, 'door_1_left_frame_profile')?.height || 0)
    adjustHinges(model, 'door_2_right_frame_profile', getParam(model.parameters!, 'door_2_right_frame_profile')?.height || 0)

    const middleVerticalFrame = getParam(model.parameters!, 'middle_vertical_frame_profile_center')
    if (middleVerticalFrame?.position) {
      updateParam(model, 'middle_vertical_frame_profile_center', { position: { ...middleVerticalFrame.position, y: (middleVerticalFrame.position.y || 0) + heightDifference } as ModelParameter['position'], height: (middleVerticalFrame.height || 0) - heightDifference })
    }

    const topFrameIds = ['door_1_top_frame_profile', 'door_2_top_frame_profile']
    topFrameIds.forEach((id) => {
      const f = getParam(model.parameters!, id)
      if (f?.position) updateParam(model, id, { position: { ...f.position, y: (f.position.y || 0) + heightDifference } as ModelParameter['position'] })
    })

    const middleHorizontalFrame = getParam(model.parameters!, 'middle_horizontal_frame_profile')
    if (middleHorizontalFrame?.position) updateParam(model, 'middle_horizontal_frame_profile', { position: { ...middleHorizontalFrame.position, y: (middleHorizontalFrame.position.y || 0) + heightDifference } as ModelParameter['position'] })

    const bottomFrameIds = ['door_1_bottom_frame_profile', 'door_2_bottom_frame_profile']
    bottomFrameIds.forEach((id) => {
      const f = getParam(model.parameters!, id)
      if (f?.position) updateParam(model, id, { position: { ...f.position, y: (f.position.y || 0) + heightDifference } as ModelParameter['position'] })
    })

    const glassPanelIds = ['door_glass_panel_1', 'door_glass_panel_2']
    glassPanelIds.forEach((id) => {
      const g = getParam(model.parameters!, id)
      if (g?.position) updateParam(model, id, { position: { ...g.position, y: (g.position.y || 0) + heightDifference } as ModelParameter['position'], height: (g.height || 0) - heightDifference })
    })

    Object.keys(preservedPositionsX).forEach((positionId) => {
      const component = getParam(model.parameters!, positionId)
      if (component?.position && preservedPositionsX[positionId] !== undefined) {
        updateParam(model, positionId, { position: { ...component.position, x: preservedPositionsX[positionId] } as ModelParameter['position'] })
      }
    })

    const updatedRightDoor = getParam(model.parameters!, 'door_glass_panel_2')
    const door2GlassPanel = getParam(model.parameters!, 'door_glass_panel_2')
    if (updatedRightDoor?.hardware?.handles && door2GlassPanel) {
      const newHeight = updatedRightDoor.height || 1765
      const updatedHandles = updatedRightDoor.hardware.handles.map((handle) => {
        const newY = Math.max(50, Math.min(newHeight - (handle.height || 200) - 50, newHeight / 2))
        return { ...handle, position: { ...handle.position, y: newY } }
      })
      updateParam(model, 'door_glass_panel_2', { hardware: { ...updatedRightDoor.hardware, handles: updatedHandles } })
    }
  }

  return model
}

const doorPanelHeight: DimensionHandler = (model, _initial, numValue, options) => {
  const { cascadeUpdates = true } = options
  const clampedHeight = Math.max(1200, Math.min(3000, numValue))
  const topFrame = getParam(model.parameters!, 'top_frame_profile')
  const doorTopPanel = getParam(model.parameters!, 'door_top_glass_panel')
  const transomeBottomFrame = getParam(model.parameters!, 'middle_horizontal_frame_profile')
  const frameHeight = topFrame?.height || 60
  const frameThickness = 60

  const totalTransomHeight = frameHeight + (doorTopPanel?.height || 200) + (transomeBottomFrame?.height || 60)
  const newTotalHeight = totalTransomHeight + clampedHeight
  const validated = validateDimensions(model.width as number, newTotalHeight)
  model.height = validated.height

  if (cascadeUpdates) {
    const preservedPositionsX: Record<string, number> = {}
    model.parameters!.forEach((param) => {
      if (param?.position?.x !== undefined) preservedPositionsX[param.positionId!] = param.position.x
    })

    const doorGlassPanelHeight = clampedHeight - frameThickness
    updateParam(model, 'door_glass_panel_1', { height: doorGlassPanelHeight })
    updateParam(model, 'door_glass_panel_2', { height: doorGlassPanelHeight })

    const doorFrames = ['door_1_left_frame_profile', 'door_1_right_frame_profile', 'door_2_left_frame_profile', 'door_2_right_frame_profile', 'middle_vertical_frame_profile_center']
    doorFrames.forEach((id) => updateParam(model, id, { height: clampedHeight }))

    recalculatePositions(model, true)

    Object.keys(preservedPositionsX).forEach((positionId) => {
      const component = getParam(model.parameters!, positionId)
      if (component?.position && preservedPositionsX[positionId] !== undefined) {
        updateParam(model, positionId, { position: { ...component.position, x: preservedPositionsX[positionId] } as ModelParameter['position'] })
      }
    })

    updateParam(model, 'door_glass_panel_1', { height: doorGlassPanelHeight })
    updateParam(model, 'door_glass_panel_2', { height: doorGlassPanelHeight })
    updateParam(model, 'left_frame_profile', { height: validated.height })
    updateParam(model, 'right_frame_profile', { height: validated.height })

    const verticalFrameHeight = validated.height - frameHeight
    updateParam(model, 'left_vertical_frame_profile', { height: verticalFrameHeight })
    updateParam(model, 'right_vertical_frame_profile', { height: verticalFrameHeight })

    doorFrames.forEach((id) => updateParam(model, id, { height: clampedHeight }))
    adjustHinges(model, 'door_1_left_frame_profile', clampedHeight)
    adjustHinges(model, 'door_2_right_frame_profile', clampedHeight)

    const rightDoor = getParam(model.parameters!, 'door_glass_panel_2')
    if (rightDoor?.hardware?.handles) {
      const updatedHandles = rightDoor.hardware.handles.map((handle) => {
        const relativeY = handle.position.y / (rightDoor.height || 1765)
        const newY = Math.max(50, Math.min(doorGlassPanelHeight - (handle.height || 200) - 50, relativeY * doorGlassPanelHeight))
        return { ...handle, position: { ...handle.position, y: newY } }
      })
      updateParam(model, 'door_glass_panel_2', { hardware: { ...rightDoor.hardware, handles: updatedHandles } })
    }
  }

  return model
}

const bottomLeftWidth: DimensionHandler = (model, _initial, numValue, options) => {
  const { cascadeUpdates = true } = options
  const frameThickness = 60
  const clampedWidth = Math.max(200, Math.min(1200, numValue))

  const leftBottomFrame = getParam(model.parameters!, 'bottom_left_frame_profile')
  const oldWidth = leftBottomFrame?.width || 440
  const widthDifference = clampedWidth - oldWidth

  updateParam(model, 'bottom_left_frame_profile', { width: clampedWidth })
  const leftFrame = getParam(model.parameters!, 'left_frame_profile')
  const leftVerticalFrame = getParam(model.parameters!, 'left_vertical_frame_profile')
  updateParam(model, 'left_glass_panel', { width: clampedWidth - (leftFrame?.width || frameThickness) - (leftVerticalFrame?.width || frameThickness) })

  if (cascadeUpdates) {
    if (leftVerticalFrame) {
      updateParam(model, 'left_vertical_frame_profile', { position: { x: clampedWidth - frameThickness, y: leftVerticalFrame.position?.y || frameThickness, z: 0 } })
    }

    const componentsToShift = [
      'door_1_bottom_frame_profile', 'door_1_left_frame_profile', 'door_1_right_frame_profile',
      'door_1_top_frame_profile', 'door_glass_panel_1', 'middle_vertical_frame_profile_center',
      'door_2_bottom_frame_profile', 'door_2_left_frame_profile', 'door_2_right_frame_profile',
      'door_2_top_frame_profile', 'door_glass_panel_2', 'right_vertical_frame_profile',
      'right_glass_panel', 'bottom_right_frame_profile', 'door_top_glass_panel',
      'middle_horizontal_frame_profile', 'right_frame_profile',
    ]
    shiftComponentsRight(model, componentsToShift, widthDifference)

    const rightFixedWidth = getParam(model.parameters!, 'bottom_right_frame_profile')?.width || 440
    const door1Width = getParam(model.parameters!, 'door_1_bottom_frame_profile')?.width || 810
    const door2Width = getParam(model.parameters!, 'door_2_bottom_frame_profile')?.width || 810
    const newTotalWidth = clampedWidth + door1Width + door2Width + rightFixedWidth
    const validated = validateDimensions(newTotalWidth, model.height as number)
    model.width = validated.width
    updateParam(model, 'top_frame_profile', { width: validated.width })
    updateParam(model, 'right_frame_profile', { position: { x: validated.width - frameThickness, y: 0, z: 0 } })

    model.doorConfiguration = { ...model.doorConfiguration!, totalDoorWidth: validated.width, leftFixedWidth: clampedWidth }
  }

  return model
}

const bottomRightWidth: DimensionHandler = (model, _initial, numValue, options) => {
  const { cascadeUpdates = true } = options
  const frameThickness = 60
  const clampedWidth = Math.max(200, Math.min(1200, numValue))

  updateParam(model, 'bottom_right_frame_profile', { width: clampedWidth })
  const rightFrame = getParam(model.parameters!, 'right_frame_profile')
  const rightVerticalFrame = getParam(model.parameters!, 'right_vertical_frame_profile')
  updateParam(model, 'right_glass_panel', { width: clampedWidth - (rightFrame?.width || frameThickness) - (rightVerticalFrame?.width || frameThickness) })

  if (cascadeUpdates) {
    const leftFixedWidth = getParam(model.parameters!, 'bottom_left_frame_profile')?.width || 440
    const door1Width = getParam(model.parameters!, 'door_1_bottom_frame_profile')?.width || 810
    const door2Width = getParam(model.parameters!, 'door_2_bottom_frame_profile')?.width || 810
    const newTotalWidth = leftFixedWidth + door1Width + door2Width + clampedWidth
    const validated = validateDimensions(newTotalWidth, model.height as number)
    model.width = validated.width
    updateParam(model, 'top_frame_profile', { width: validated.width })
    updateParam(model, 'right_frame_profile', { position: { x: validated.width - frameThickness, y: 0, z: 0 } })

    model.doorConfiguration = { ...model.doorConfiguration!, totalDoorWidth: validated.width, rightFixedWidth: clampedWidth }
  }

  return model
}

export const model14DimensionHandlers: Record<string, DimensionHandler> = {
  totalWidth,
  totalHeight,
  handleHeight,
  bottomFrame1Width,
  bottomFrame2Width,
  transomHeight,
  doorPanelHeight,
  bottomLeftWidth,
  bottomRightWidth,
}
